import { createReducer } from 'modules/redux-utils'
import t from './actionTypes'

// initialState
const initialState = {
  userId: null,
  qleGroupNo: null,
  qleGroup: null,
  user: {},
  ichra: {},
  providers: [],
  clientSettings: {},
  clientBenefitsEnrollment: {},
  enrollmentResponses: [],
  enrollmentSession: {},
  memberships: [],
  isPaused: false,
  isPausedForActor: false,
  userInitialized: false,
  updatingUser: false,
  scrolledToBottom: false,
  errors: [],
  location: null,
  enrollmentCutoffTime: '',
}

export default createReducer(initialState, {
  [t.SET_USER_ID]: (state, payload) => ({
    ...state,
    userId: payload,
  }),
  [t.SET_QLE_GROUP_NO]: (state, payload) => ({
    ...state,
    qleGroupNo: payload,
  }),
  [t.SET_QLE_GROUP]: (state, payload) => ({
    ...state,
    qleGroup: payload,
  }),
  [t.LOAD_USER]: (state, payload) => ({
    ...state,
    user: payload,
  }),
  [t.LOAD_PAYROLL]: (state, payload) => ({
    ...state,
    payroll: payload,
  }),
  [t.LOAD_CLIENT_SETTINGS]: (state, payload) => ({
    ...state,
    clientSettings: payload,
  }),
  [t.LOAD_ENROLLMENT_CUTOFF_TIME]: (state, payload) => ({
    ...state,
    enrollmentCutoffTime: payload,
  }),
  [t.LOAD_CLIENT_BENEFITS_ENROLLMENT]: (state, payload) => ({
    ...state,
    clientBenefitsEnrollment: payload,
  }),
  [t.LOAD_ENROLLMENT_RESPONSES]: (state, payload) => ({
    ...state,
    enrollmentResponses: payload.map((res) =>
      // solve for difference in 2017 / 2018+ data shapes
      res.Responders.length && res.Responders[0].Response
        ? {
            ...res,
            Responders: res.Responders.reduce((prev, curr) => {
              if (curr.Response === 'Yes') {
                return [...prev, curr.PersonPublicKey]
              }
              return prev
            }, []),
            Response: res.Responders.some((responder) => responder.Response === 'Yes')
              ? 'Yes'
              : 'No',
          }
        : res
    ),
  }),
  [t.LOAD_ENROLLMENT_SESSION]: (state, payload) => ({
    ...state,
    enrollmentSession: payload,
  }),
  [t.LOAD_PREVIOUS_ENROLLMENT]: (state, payload) => ({
    ...state,
    previousEnrollment: payload,
  }),
  [t.UPDATING_USER]: (state) => ({
    ...state,
    updatingUser: true,
    errors: [],
  }),
  [t.LOAD_MEMBERSHIPS]: (state, payload) => ({
    ...state,
    memberships: Array.isArray(payload) ? payload : [],
  }),
  [t.LOAD_PAUSED_STATE]: (state, payload) => ({
    ...state,
    isPaused: !!(payload && payload.isPaused),
    isPausedForActor: !!(payload && payload.isPausedForActor),
  }),
  [t.USER_INITIALIZED]: (state) => ({
    ...state,
    userInitialized: true,
    updatingUser: false,
    // Keep bootstrap errors so a failed enrollment fetch still surfaces
    // UserSessionErrorView (UPDATING_USER already cleared prior-session errors).
  }),
  [t.USER_INITIALIZE_ERROR]: (state, payload) => ({
    ...state,
    errors: [...state.errors, payload],
  }),
  [t.SCROLLED_TO_BOTTOM]: (state) => ({
    ...state,
    scrolledToBottom: true,
  }),
  [t.SET_PROVIDER_QUESTION]: (state, payload) => ({
    ...state,
    enrollmentSession: {
      ...state.enrollmentSession,
      ProviderSearchCheck: payload,
    },
  }),
  [t.LOAD_ICHRA_INFORMATION]: (state, payload) => ({
    ...state,
    ichra: payload,
  }),
  [t.SET_USER_LOCATION]: (state, payload) => ({
    ...state,
    location: {
      ...payload,
    },
  }),
  [t.SET_FAMILY_GROUPS]: (state, payload) => ({
    ...state,
    enrollmentSession: {
      ...state.enrollmentSession,
      FamilyGroups: payload,
    },
  }),
  [t.DELETE_FAMILY_GROUPS]: (state) => {
    const { FamilyGroups, ...session } = state.enrollmentSession
    return {
      ...state,
      enrollmentSession: session,
    }
  },
  [t.DELETE_ALL_FROM_PROVIDER]: (state, payload) => ({
    ...state,
    providers: state.providers.filter((provider) => provider.id !== payload.ProviderPublicKey),
  }),
  [t.LOAD_PROVIDERS]: (state, payload) => ({
    ...state,
    providers: payload,
  }),
  [t.REMOVE_PROVIDER]: (state, payload) => {
    const isEmpty =
      state.providers
        .find((provider) => provider.id === payload.ProviderPublicKey)
        .selected.filter((person) => person.id !== payload.PersonPublicKey).length === 0

    const providers = isEmpty
      ? state.providers.filter((provider) => provider.id !== payload.ProviderPublicKey)
      : state.providers.map((provider) => ({
          ...provider,
          selected:
            provider.id === payload.ProviderPublicKey
              ? provider.selected.filter((person) => person.id !== payload.PersonPublicKey)
              : provider.selected,
        }))

    return {
      ...state,
      providers,
    }
  },
  [t.ADD_PROVIDER]: (state, payload) => {
    const providerExists = state.providers.find(
      (provider) => provider.id === payload.ProviderPublicKey
    )
    // payload.ProviderPublicKey is the provider row to key/match against —
    // for a fresh add it also happens to be the exact NPI the association is
    // saved under server-side (associatePersonToProvider sends this same
    // value), so it's carried on the optimistic selected entry too, matching
    // the shape getPersonsByProvider builds for backend-loaded persons.
    // Without it, removeAllFromProvider's savedNpis (built from
    // provider.selected) would miss this person's NPI whenever a mix of
    // backend-loaded and optimistically-added persons share a provider,
    // silently leaving their association in place after "Remove All".
    //
    // A failed-remove rollback reuses this same action to restore a
    // pre-existing association, whose saved NPI can differ from the
    // provider's current-primary id after Ideon rotates it — that path
    // passes the real saved NPI explicitly via payload.providerPublicKey,
    // which takes precedence here.
    const selectedEntry = {
      id: payload.person.Id,
      name: payload.person.FullName,
      providerPublicKey: payload.providerPublicKey ?? payload.ProviderPublicKey,
    }

    const providers = providerExists
      ? state.providers.map((provider) => ({
          ...provider,
          selected:
            provider.id === payload.ProviderPublicKey
              ? [...provider.selected, selectedEntry]
              : provider.selected,
        }))
      : [
          ...state.providers,
          {
            ...payload.Provider,
            selected: [selectedEntry],
          },
        ]
    return {
      ...state,
      providers,
    }
  },
})
